import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type { CatalogIndex } from '@safetyhub/shared';
import { adminApi } from '../../lib/api';
import type { FormImportProgress } from '../../lib/api';
import { formatElapsed } from '../../hooks/useElapsedSeconds';
import { formImportSheetChoices } from './formImportSheetChoices';
import { describeFormImportOutcome, formImportTotalCount } from './formImportOutcome';
import { applyAllRepeatHints, writeFormLabelsOf } from './formImportOps';
import { toUpdateBody } from './templateUpdateBody';
import type { FormImportJobState } from './useFormImportJob';

/**
 * 양식(이미지/PDF/엑셀/한글) 업로드 → AI 분석 → 템플릿 초안 생성 모달.
 *
 * 설계 정본: docs/decisions/2026-07-27-composer-form-import-ir.md
 * HWP 경로: docs/decisions/2026-10-04-hwp-form-import.md (P8)
 * "완전 자동"이 아니라 **초안 스캐폴드 생성기**다. 생성된 템플릿은 항상 비공개(enabled=false)이고,
 * 반복 영역 같은 판단이 필요한 항목은 적용하지 않고 **제안 목록**으로만 보여 준다(사람 확정 원칙).
 *
 * Job의 소유자는 이 모달이 아니라 페이지다(useFormImportJob) — 폴링·새로고침 복구·수명주기가
 * 모두 훅에 있고, 여기서는 그 상태의 **표시자** 역할과 업로드 시작 트리거만 맡는다. 그래서
 * 분석 중에 창을 닫아도 분석은 계속되고(헤더 pill이 진행을 보여 준다) 다시 열면 이어서 보인다.
 * 파일 선택·시트 선택처럼 업로드 직전까지만 쓰는 입력 상태는 이 모달의 것이다.
 */

/** 서버 stage → 진행 화면 라벨. 서버 FormImportProgress.stage와 1:1. */
const STAGE_LABELS: Record<FormImportProgress['stage'], string> = {
  uploading: '업로드 중',
  analyzing: 'AI로 양식 분석 중',
  validating: '분석 결과 검증 중',
  creating: '템플릿 초안 생성 중',
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * 확장자 병기 — 일부 환경이 MIME을 다르게 보고해 파일 선택창에서 걸러지는 것 방지.
 * HWP는 한컴·OS·브라우저가 서로 다른 MIME을 보내므로 확장자 병기가 **특히 필수**다
 * (서버 `HWP_MIME_TYPES` + `isHwpUpload`와 대칭). `.hwpx`도 고를 수 있게 둔다 —
 * 서버가 "HWPX는 .hwp로 저장해주세요"라고 알려 주는 쪽이, 선택창에서 조용히 사라져
 * 왜 안 되는지 모르는 것보다 낫다.
 *
 * ⭐ **이 선언이 지원 형식의 기계적 정본이다** — `formImportSupportedFormats.guard.test.ts`가 여기서
 * 토큰을 뽑아 **분모**로 쓰고, 아래 안내 줄과 `TemplateExplorer`의 진입 메뉴 라벨 양쪽에 그 형식의
 * 낱말이 있는지 센다(2026-10-05 신설 — P8에서 진입 라벨 하나가 낡은 채 남아 dev 실측에서야 드러났다).
 * 그러니 **여기에 형식을 더하면 그 가드가 「매핑 없는 형식」으로 먼저 막는다** — 화면 어휘를 정하고
 * 그 표에 더하라. 화면에 열거하지 않는 것이 옳은 토큰(`.hwpx`)은 표에서 사유와 함께 null로 둔다.
 */
const ACCEPT_MIME = `image/jpeg,image/png,application/pdf,${XLSX_MIME},.xlsx,.hwp,.hwpx`;

/** 시트 목록 조회 실패의 일반 문구 — 서버가 사유를 주지 않았을 때만 쓴다. */
const SHEET_PROBE_FALLBACK_MESSAGE = '시트 목록을 읽지 못했습니다.';

/**
 * 시트 1장당 예상 소요(초) — 2026-08-19 실측 45~50초(입력 28개·표 2개·25행×11열 양식).
 * 서버 타임아웃 상한(5분)은 실소요가 아니므로 안내에 쓰지 않는다. 시트를 많이 고르면
 * 총 소요가 수십 분이 되는데, 시작 버튼을 누르기 **전에** 그 규모를 알려야 취소로 되돌아오는
 * 왕복이 줄어든다.
 */
const SECONDS_PER_SHEET = 48;

/** 예상 소요를 사람이 읽는 대략치로 — 분 단위 반올림(정확한 척하지 않는다). */
function describeEstimate(sheetCount: number): string {
  const totalSeconds = Math.max(1, sheetCount) * SECONDS_PER_SHEET;
  if (totalSeconds < 90) return '약 1분';
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `약 ${minutes}분`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `약 ${hours}시간` : `약 ${hours}시간 ${rest}분`;
}

/** 서버 isXlsxUpload과 동형 — 비표준 MIME이어도 확장자가 .xlsx면 xlsx로 취급한다. */
function isXlsxFile(file: File): boolean {
  return file.type === XLSX_MIME || /\.xlsx$/i.test(file.name);
}

/** 초안 카드의 반복 제안 일괄 적용 상태(카드별). */
type RepeatApplyState =
  | { status: 'applying' }
  | { status: 'done'; applied: number; failures: string[] }
  | { status: 'error'; message: string }
  /** 에디터에서 편집 중이라 서버 직접 적용을 막은 상태 — 재시도해도 같으므로 안내만. */
  | { status: 'blocked'; message: string };

/** 편집 중인 초안에는 서버 직접 PATCH를 하지 않는다 — 안내 문구(사용자 행동 지시 포함). */
const REPEAT_APPLY_BLOCKED_MESSAGE =
  '이 초안은 지금 에디터에서 편집 중입니다 — 에디터의 반복 제안 배지로 적용하세요.';

interface FormImportModalProps {
  /** 열림 여부. false면 렌더 안 함. */
  open: boolean;
  /** Job 상태 — 페이지가 소유한다(useFormImportJob). 헤더 pill과 같은 값을 본다. */
  job: FormImportJobState;
  /** 미저장 변경 경고 등 amber 강조 안내(있을 때만 표시). */
  warning?: string;
  /**
   * 지금 에디터에 로드된 템플릿 key — 그 초안에는 반복 제안을 **서버에 직접 적용하지 않는다**.
   * 에디터의 미저장 편집분과 충돌하고, 이후 에디터 저장이 방금 적용분을 통째로 덮어쓴다.
   */
  currentKey?: string | null;
  /** 반복 제안 적용 시 데이터셋 필드 라벨 산출용 카탈로그 색인(없어도 동작 — 라벨만 덜 채워짐). */
  catalogIndex?: CatalogIndex;
  /** 닫기 — 분석 중이면 Job을 남긴 채 창만 닫는다(훅이 판단). */
  onClose: () => void;
  /** 업로드 시작. 시트 미선택이면 빈 배열(서버 자동 선택). 여러 개면 서버가 순차 변환한다. */
  onUpload: (file: File, sheetNames: string[]) => void;
  /** "다시 시도" — 표시 중인 완료/실패본을 버리고 파일 선택부터. */
  onRetry: () => void;
  /** 진행 중 Job 취소 요청 — 즉시 끊기지 않는다(현재 시트 종료 후 반영). */
  onCancelJob: () => void;
  /** 생성된 초안을 에디터에서 열기 — 부모가 목록 갱신 + 로드를 수행한다. */
  onOpenEditor: (templateKey: string) => void;
}

export function FormImportModal({
  open,
  job,
  warning,
  currentKey,
  catalogIndex,
  onClose,
  onUpload,
  onRetry,
  onCancelJob,
  onOpenEditor,
}: FormImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  // 원문 복사 완료 표시(버튼 라벨 전환) — 새 시도/재열림 때 리셋.
  const [rawCopied, setRawCopied] = useState(false);
  /** 엑셀 시트 선택 후보(이름) — 시트 2개 이상일 때만 목록(그 외 null → UI 숨김). */
  const [sheetChoices, setSheetChoices] = useState<string[] | null>(null);
  /**
   * 시트 목록 조회 실패 사유 — 예전에는 console.warn으로 삼키고 자동 선택으로 조용히 폴백했다.
   * 그러면 시트가 여럿인 파일에서 선택 UI가 말없이 사라져, 사용자는 "고를 수 없는 파일"로
   * 오해한다. 업로드는 계속 허용하되(자동 선택으로 진행 가능) 그 사실을 눈에 보이게 만든다.
   */
  const [sheetProbeError, setSheetProbeError] = useState<string | null>(null);
  /**
   * 선택된 시트 이름들 — 빈 배열은 "자동(내용이 있는 첫 시트)" = 미전송.
   *
   * 단일 `<select>`에서 체크박스 목록으로 바꿨다. 47시트짜리 파일에서 드래그 다중 선택
   * (`<select multiple>`)은 실수로 전체 선택이 풀리기 쉽고, 지금 몇 개를 골랐는지가 보이지
   * 않는다 — 시작 한 번에 수십 분이 걸리는 작업에서는 그 확인이 필요하다.
   */
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  /** 시트 이름 필터 — 47개 목록에서 원하는 것을 찾는 비용을 낮춘다. */
  const [sheetFilter, setSheetFilter] = useState('');
  /**
   * 초안별 반복 제안 일괄 적용 상태 — key=초안 templateKey. 모달을 닫아도 컴포넌트는 살아 있어
   * (Modal이 open=false에서 내용만 감춘다) 다시 열어도 방금 적용한 배지가 남는다. 파일 선택
   * 리셋(resetFileInputs)과 달리 **열림 때 지우지 않는다** — 지우면 분리했다 돌아온 사용자가
   * 이미 적용한 초안에 다시 적용을 누르게 된다.
   */
  const [repeatApply, setRepeatApply] = useState<Record<string, RepeatApplyState>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 시트 목록 비동기 조회의 stale 응답 가드 — 파일을 바꾸면 이전 조회 결과를 버린다. */
  const sheetProbeFileRef = useRef<File | null>(null);

  const { phase, progress, result, error, rawResponse, elapsedSeconds } = job;

  /** 파일·시트 선택 입력 리셋 — 재열림/다시 시도 공용. Job 상태는 훅 소유라 여기서 만지지 않는다. */
  const resetFileInputs = useCallback(() => {
    setFile(null);
    setRawCopied(false);
    sheetProbeFileRef.current = null;
    setSheetChoices(null);
    setSheetProbeError(null);
    setSelectedSheets([]);
    setSheetFilter('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // 열릴 때 입력 상태 초기화(직전 파일 잔존 방지). 진행 중 Job을 보러 다시 연 경우에도
  // 안전하다 — 지우는 것은 파일 입력뿐이고 진행 상황은 훅이 들고 있다.
  useEffect(() => {
    if (!open) return;
    resetFileInputs();
  }, [open, resetFileInputs]);

  /**
   * xlsx 선택 시 시트 이름 목록을 미리 조회해 선택 UI 노출 여부를 정한다.
   * 전용 경량 API(sheet-names) — 서버가 격자를 만들지 않아 시트가 많은 파일도 통과한다.
   * 실패는 삼키지 않고 sheetProbeError로 모아 경고 배너로 보여 준다(업로드는 계속 가능).
   */
  const probeSheetChoices = useCallback(async (target: File) => {
    sheetProbeFileRef.current = target;
    try {
      const res = await adminApi.getFormImportSheetNames(target);
      if (sheetProbeFileRef.current !== target) return; // 파일이 바뀜 — stale 응답 버림
      if (res.success && res.data) {
        setSheetChoices(formImportSheetChoices(res.data.sheetNames));
        setSheetProbeError(null);
        return;
      }
      // 서버가 사유를 준 실패(success=false) — 종전에는 warn조차 없이 사라지던 분기.
      setSheetChoices(null);
      setSheetProbeError(res.error?.message ?? SHEET_PROBE_FALLBACK_MESSAGE);
    } catch (err) {
      if (sheetProbeFileRef.current !== target) return;
      setSheetChoices(null);
      // 서버가 준 안내 문구(상한 초과·손상 등)를 그대로 보여 준다 — 자체 문구로 덮으면
      // 사용자가 실제 원인(예: 시트 수 상한)을 알 수 없다.
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? (err as Error).message;
      setSheetProbeError(message || SHEET_PROBE_FALLBACK_MESSAGE);
    }
  }, []);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0] ?? null;
    setFile(next);
    // 파일이 바뀌면 시트 선택 상태 리셋(이전 파일의 시트 목록/선택/경고 잔존 방지)
    sheetProbeFileRef.current = null;
    setSheetChoices(null);
    setSheetProbeError(null);
    setSelectedSheets([]);
    setSheetFilter('');
    if (next && isXlsxFile(next)) {
      void probeSheetChoices(next);
    }
  };

  /** 필터에 걸린 시트만 — 전체 선택/해제도 이 부분집합에만 작용한다(보이는 것에만 작용). */
  const visibleSheets = useMemo(() => {
    if (!sheetChoices) return [];
    const needle = sheetFilter.trim().toLowerCase();
    if (!needle) return sheetChoices;
    return sheetChoices.filter((name) => name.toLowerCase().includes(needle));
  }, [sheetChoices, sheetFilter]);

  const selectedSet = useMemo(() => new Set(selectedSheets), [selectedSheets]);
  const allVisibleSelected =
    visibleSheets.length > 0 && visibleSheets.every((name) => selectedSet.has(name));

  const toggleSheet = useCallback((name: string) => {
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  }, []);

  /** 보이는 시트 전체 선택/해제 — 47개짜리 파일에서 하나씩 누르게 하지 않는다. */
  const toggleAllVisible = useCallback(() => {
    setSelectedSheets((prev) => {
      const visible = new Set(visibleSheets);
      const everySelected = visibleSheets.length > 0 && visibleSheets.every((n) => prev.includes(n));
      if (everySelected) return prev.filter((n) => !visible.has(n));
      // 선택 순서 = 변환 순서라 기존 선택 뒤에 붙인다(목록 순서로 재정렬하지 않는다).
      return [...prev, ...visibleSheets.filter((n) => !prev.includes(n))];
    });
  }, [visibleSheets]);

  const handleUpload = useCallback(() => {
    if (!file) return;
    onUpload(file, selectedSheets);
  }, [file, selectedSheets, onUpload]);

  const handleRetry = useCallback(() => {
    resetFileInputs();
    // 새 시도 = 새 결과 — 직전 결과 카드의 적용 상태를 들고 가면 다른 초안의 배지가 된다.
    setRepeatApply({});
    onRetry();
  }, [resetFileInputs, onRetry]);

  // 실패 원문 복사 — AI 프롬프트(FORM_IMPORT) 튜닝 시 원문을 그대로 옮겨 붙이는 품질 루프용.
  const handleCopyRaw = () => {
    if (!rawResponse) return;
    void navigator.clipboard
      .writeText(rawResponse)
      .then(() => setRawCopied(true))
      .catch(() => {});
  };

  const handleOpenEditor = () => {
    if (!result) return;
    onOpenEditor(result.templateKey);
  };

  /**
   * 초안 하나의 **반복 영역 제안 전체**를 적용하고 저장한다 — 에디터에 열지 않고.
   *
   * 결과 목록의 summary.repeatHints에는 `tableNodeId`가 없다(rowStart/rowEnd/reason만) — 적용에
   * 필요한 표 식별자는 **초안 템플릿의 importMeta.repeatHints**에만 있다. 그래서 개수는 summary로
   * 보여 주되 적용은 반드시 템플릿을 GET해서 그 importMeta로 한다.
   *
   * 저장은 에디터 저장 경로와 같은 필드 집합(toUpdateBody) — 손으로 필드를 고르면 dataSources나
   * importMeta 중 하나가 빠져 적용이 조용히 사라진다.
   *
   * ⚠️ 부분 성공을 살린다. 힌트 하나가 무효(표 구조가 바뀜 등)인 것은 정상 케이스라, 전부 아니면
   * 전무로 처리하면 나머지를 사람이 다시 하나씩 눌러야 한다. 실패 건은 importMeta에 남으므로
   * 에디터에서 그대로 다시 판단할 수 있다.
   */
  const handleApplyRepeatHints = useCallback(
    async (draftKey: string) => {
      if (currentKey === draftKey) {
        setRepeatApply((prev) => ({
          ...prev,
          [draftKey]: { status: 'blocked', message: REPEAT_APPLY_BLOCKED_MESSAGE },
        }));
        return;
      }
      setRepeatApply((prev) => ({ ...prev, [draftKey]: { status: 'applying' } }));
      const fail = (message: string) =>
        setRepeatApply((prev) => ({ ...prev, [draftKey]: { status: 'error', message } }));
      try {
        const got = await adminApi.getReportTemplate(draftKey);
        if (!got.success || !got.data) {
          fail(got.error?.message ?? '초안을 불러오지 못했습니다.');
          return;
        }
        const labels = writeFormLabelsOf(got.data, catalogIndex);
        const { next, applied, failures } = applyAllRepeatHints(got.data, labels);
        const failureMessages = failures.map((f) => f.error);
        if (applied.length === 0) {
          // 저장할 변경이 없다 — PATCH를 보내지 않는다(무변경 저장은 updatedAt만 흔든다).
          setRepeatApply((prev) => ({
            ...prev,
            [draftKey]: { status: 'done', applied: 0, failures: failureMessages },
          }));
          return;
        }
        const saved = await adminApi.updateReportTemplate(draftKey, toUpdateBody(next));
        if (!saved.success) {
          fail(saved.error?.message ?? '적용한 내용을 저장하지 못했습니다.');
          return;
        }
        setRepeatApply((prev) => ({
          ...prev,
          [draftKey]: { status: 'done', applied: applied.length, failures: failureMessages },
        }));
      } catch (err) {
        fail((err as Error).message || '반복 제안 적용 중 오류가 발생했습니다.');
      }
    },
    [currentKey, catalogIndex],
  );

  const importing = phase === 'importing';
  const stage = progress?.stage ?? 'uploading';
  /**
   * AI 응답을 기다리는 구간 — 서버가 이 구간의 진행률을 모른다(analyzing 진입 시 percent를
   * 30으로 올린 뒤 응답이 올 때까지 그대로). 확정 바를 두면 30%에서 멈춘 것으로 읽히므로
   * 불확정 바 + 경과 시간으로 대체한다. 나머지 stage는 확정 바가 정직하다 —
   * uploading은 실제 전송 구간이고, validating(JSON 파싱·zod)·creating(템플릿 INSERT)은 순식간.
   */
  const waitingOnAi = stage === 'analyzing';
  const percent = Math.min(100, Math.max(0, progress?.percent ?? 5));
  const batch = progress?.batch;
  const cancelling = progress?.cancelRequested === true;
  /** 종료(완료/취소) 화면에서 항목별 결과를 보여 줄지 — 단건이면 종전처럼 요약만 보여 준다. */
  const outcomes = progress?.sheets ?? [];
  const isBatchResult = formImportTotalCount(progress) > 1;

  /**
   * 항목별 결과 목록 — **결과 화면과 실패 화면이 같은 목록을 쓴다.**
   *
   * 종전에는 결과(phase='result') 분기 안에만 있어서, 전건 실패로 실패 화면에 떨어지면
   * `progress.sheets`에 시트별 사유가 **이미 와 있는데도** 마지막 한 건의 error 문자열만 보였다
   * (47시트가 전부 실패하면 어느 시트가 왜 실패했는지 알 방법이 없다). 실패 항목은 draftKey가
   * 없어 [열기]·[반복 제안 적용] 버튼이 애초에 그려지지 않으므로 그대로 재사용할 수 있다.
   *
   * 스크롤 박스로 가두는 이유: 아래의 안내와 푸터 버튼이 목록에 밀려 화면 밖으로 나가면
   * "열기"조차 찾지 못한다.
   */
  const outcomeList =
    outcomes.length > 0 ? (
      <ul className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded border border-gray-200">
        {outcomes.map((outcome, i) => {
          // 좁히기를 한 번에 끝내 아래 JSX에서 캐스트가 필요 없게 한다.
          const draftKey = outcome.status === 'completed' ? outcome.templateKey : undefined;
          const summary = outcome.summary;
          // ⚠️ 여기 repeatHints에는 tableNodeId가 없다(개수 표시 전용) — 적용은
          //    handleApplyRepeatHints가 템플릿의 importMeta로 한다.
          const hintCount = summary?.repeatHints.length ?? 0;
          const unsupportedCount = summary?.unsupported.length ?? 0;
          const applyState = draftKey ? repeatApply[draftKey] : undefined;
          const applying = applyState?.status === 'applying';
          const applyDone = applyState?.status === 'done';
          return (
            <li
              key={`${outcome.sheetName ?? 'item'}-${i}`}
              className="flex items-start justify-between gap-2 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <span className="block truncate font-medium text-gray-800">
                  {outcome.sheetName ?? '업로드한 양식'}
                </span>
                {draftKey && summary ? (
                  // truncate를 쓰지 않는다 — 제안 건수가 붙으면서 줄이 길어지는데, 잘리는
                  // 쪽이 하필 key(어느 초안인지)다. 두 줄이 되더라도 다 보이는 게 낫다
                  // (목록 전체 높이는 스크롤 박스가 잡는다).
                  <span className="block text-xs leading-relaxed text-gray-500">
                    입력 필드 {summary.fieldCount}개 · 표 {summary.tableCount}개
                    {hintCount > 0 ? ` · 반복 제안 ${hintCount}건` : ''}
                    {unsupportedCount > 0 ? ` · 확인 필요 ${unsupportedCount}건` : ''} · {draftKey}
                  </span>
                ) : (
                  <span className="block text-xs leading-relaxed text-red-600">
                    {outcome.status === 'cancelled' ? '취소됨' : '실패'} —{' '}
                    {outcome.error ?? '사유를 알 수 없습니다.'}
                  </span>
                )}
                {draftKey && summary && (hintCount > 0 || unsupportedCount > 0) ? (
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">
                      자세히
                    </summary>
                    {hintCount > 0 ? (
                      <ul className="mt-1 space-y-0.5 rounded bg-amber-50 px-2 py-1.5 text-xs leading-relaxed text-amber-700">
                        {summary.repeatHints.map((hint, hi) => (
                          <li key={`${hint.rowStart}-${hint.rowEnd}-${hi}`}>
                            · {hint.rowStart}~{hint.rowEnd}행이 반복 영역으로 보입니다 (
                            {hint.reason})
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {unsupportedCount > 0 ? (
                      <ul className="mt-1 space-y-0.5 rounded bg-gray-50 px-2 py-1.5 text-xs leading-relaxed text-gray-600">
                        {summary.unsupported.map((item, ui) => (
                          <li key={`${ui}-${item}`}>· {item}</li>
                        ))}
                      </ul>
                    ) : null}
                  </details>
                ) : null}
                {applyState && applyState.status !== 'applying' ? (
                  <span
                    className={
                      applyState.status === 'done' && applyState.applied > 0
                        ? 'mt-0.5 block text-xs leading-relaxed text-green-700'
                        : applyState.status === 'done'
                          ? 'mt-0.5 block text-xs leading-relaxed text-gray-500'
                          : 'mt-0.5 block text-xs leading-relaxed text-red-600'
                    }
                  >
                    {applyState.status === 'done'
                      ? `반복영역 적용 ${applyState.applied}건` +
                        (applyState.failures.length > 0
                          ? ` · 건너뜀 ${applyState.failures.length}건(${applyState.failures.join(' / ')})`
                          : '') +
                        (applyState.applied > 0
                          ? ' — 에디터에서 행 데이터셋 위젯 배치를 마무리하세요.'
                          : '')
                      : applyState.message}
                  </span>
                ) : null}
              </div>
              {draftKey ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  {hintCount > 0 ? (
                    // 적용 성공 후에는 다시 누를 것이 없다(힌트가 소진됨) — 비활성 + 라벨 전환.
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={applying || applyDone}
                      onClick={() => void handleApplyRepeatHints(draftKey)}
                    >
                      {applying ? '적용 중…' : applyDone ? '적용됨' : '반복 제안 적용'}
                    </Button>
                  ) : null}
                  <Button variant="secondary" size="sm" onClick={() => onOpenEditor(draftKey)}>
                    열기
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    ) : null;

  /**
   * 실패 화면이 배치인가 — counts(isBatchResult)와 항목 수를 함께 본다. 전건 실패 Job이
   * completedCount를 채우지 않고 오면 counts만으로는 total이 0이라 단건으로 오인한다.
   */
  const isBatchFailure = isBatchResult || outcomes.length > 1;

  /**
   * 실패 화면의 머리 문장 — 배치에서는 마지막 한 건의 error 문자열이 배치 전체를 대표하지
   * 못한다(47시트가 전부 실패해도 사유는 한 줄뿐이었다). 배치면 요약 문장(N개 중 M개)을 쓰고
   * 시트별 사유는 아래 outcomeList가 맡는다. 단건은 종전대로 서버가 준 사유 한 줄
   * (단건은 목록을 따로 그리지 않는다 — 같은 사유를 두 번 쓰게 된다).
   */
  const errorHeadline = isBatchFailure
    ? describeFormImportOutcome(progress)
    : (error ?? '양식 분석에 실패했습니다.');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="양식 업로드로 시작"
      size="lg"
      // 분석 중 닫기를 막지 않는다 — Job은 페이지가 소유하므로 창을 닫아도 폴링이 끊기지 않는다
      // (종전에는 모달이 폴링을 들고 있어 닫으면 진행을 놓쳤고, 그래서 닫기 자체를 막았다).
      footer={
        phase === 'result' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              닫기
            </Button>
            {/* 배치 결과는 어느 초안을 열지 목록에서 고른다 — 푸터의 단수 버튼이 남아 있으면
                "대표 1건"을 여는 동작이 목록과 어긋나 보인다. */}
            {isBatchResult ? null : (
              <Button variant="primary" onClick={handleOpenEditor}>
                에디터에서 열기
              </Button>
            )}
          </>
        ) : phase === 'error' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              닫기
            </Button>
            <Button variant="primary" onClick={handleRetry}>
              다시 시도
            </Button>
          </>
        ) : importing ? (
          <>
            {/* 분리(detach) 어포던스 — 닫아도 분석이 계속된다는 것을 버튼 문구로 알린다. */}
            <Button variant="secondary" onClick={onClose}>
              백그라운드에서 계속
            </Button>
            {/* 변환 취소는 danger가 아니다 — 이미 만들어진 초안을 지우지 않는다. 확인 모달도
                두지 않는다(되돌릴 수 없는 파괴가 아니고, 잘못 눌러도 다시 시작하면 된다). */}
            <Button variant="secondary" onClick={onCancelJob} disabled={cancelling}>
              {cancelling ? '취소 중…' : '변환 취소'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              취소
            </Button>
            <Button variant="primary" onClick={handleUpload} disabled={!file}>
              {selectedSheets.length > 1 ? `시트 ${selectedSheets.length}개 분석 시작` : '분석 시작'}
            </Button>
          </>
        )
      }
    >
      {phase === 'idle' ? (
        <div className="space-y-3">
          {warning ? (
            <p className="text-xs font-medium leading-relaxed text-amber-600">{warning}</p>
          ) : null}
          <p className="text-sm leading-relaxed text-gray-600">
            보고서 양식 이미지·PDF·엑셀·한글 문서를 올리면 AI가 표·라벨·입력칸을 읽어{' '}
            <b>템플릿 초안</b>을 만듭니다. 초안은 비공개로 생성되며, 에디터에서 다듬고
            시뮬레이션으로 확인한 뒤 공개하세요.
          </p>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="form-import-file-input"
            >
              양식 파일
            </label>
            <input
              id="form-import-file-input"
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_MIME}
              onChange={handleSelectFile}
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-sm file:text-gray-700 hover:file:bg-gray-200"
            />
          </div>
          {sheetProbeError ? (
            // 업로드를 막지 않는 경고 — 시트를 고를 수 없을 뿐, 자동 선택으로 진행은 가능하다.
            <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
              시트 목록을 불러오지 못해 <b className="font-medium">변환할 시트를 고를 수 없습니다</b>{' '}
              — 자동(내용이 있는 첫 시트)으로 진행됩니다. ({sheetProbeError})
            </p>
          ) : null}
          {sheetChoices ? (
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-gray-700">변환할 시트</span>
                <span className="text-xs tabular-nums text-gray-500">
                  {selectedSheets.length > 0
                    ? `${selectedSheets.length}개 선택 · ${describeEstimate(selectedSheets.length)} 예상`
                    : `전체 ${sheetChoices.length}개`}
                </span>
              </div>
              <div className="mb-1.5 flex items-center gap-2">
                <input
                  type="search"
                  value={sheetFilter}
                  onChange={(e) => setSheetFilter(e.target.value)}
                  placeholder="시트 이름으로 찾기"
                  aria-label="시트 이름으로 찾기"
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={toggleAllVisible}
                  disabled={visibleSheets.length === 0}
                  className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  {allVisibleSelected ? '전체 해제' : '전체 선택'}
                </button>
              </div>
              {/* 47시트 파일이 실측 사례라 목록은 스크롤 박스로 가둔다 — 모달이 화면을 넘기면
                  아래의 시작 버튼과 안내가 함께 밀려나 규모를 확인하지 못한 채 시작하게 된다. */}
              <div className="max-h-48 overflow-y-auto rounded border border-gray-300">
                {visibleSheets.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-500">
                    이름이 &lsquo;{sheetFilter}&rsquo;인 시트가 없습니다.
                  </p>
                ) : (
                  visibleSheets.map((name, i) => (
                    <label
                      key={`${name}-${i}`}
                      className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSet.has(name)}
                        onChange={() => toggleSheet(name)}
                        className="shrink-0"
                      />
                      <span className="truncate">{name}</span>
                    </label>
                  ))
                )}
              </div>
              {/* 줄·칸 수를 함께 보여 주던 종전 표기는 뺐다 — 경량 판독은 격자를 만들지 않아
                  그 값을 알 수 없다(알려면 시트 수 상한에 다시 걸린다). */}
              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                표지·빈 시트도 목록에 포함됩니다. 아무것도 고르지 않으면 내용이 있는 첫 시트
                하나만 변환합니다. 여러 개를 고르면 <b className="font-medium">시트마다 초안이
                하나씩</b> 만들어집니다.
              </p>
              {selectedSheets.length > 1 ? (
                // 시작 전에 규모를 알린다 — 47시트면 40분 가까이 걸리는데, 그 사실을 시작한
                // 뒤에 알게 되면 취소하고 다시 고르는 왕복이 생긴다.
                <p className="mt-1.5 rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
                  시트 {selectedSheets.length}개를 <b className="font-medium">차례로</b> 변환합니다
                  — 시트당 약 1분, 모두 합쳐 {describeEstimate(selectedSheets.length)} 걸립니다.
                  시작한 뒤에도 멈출 수 있지만,{' '}
                  <b className="font-medium">처리 중인 시트가 끝나야 중단</b>됩니다(최대 1분).
                  그때까지 만들어진 초안은 그대로 남습니다.
                </p>
              ) : null}
            </div>
          ) : null}
          <ul className="space-y-1 rounded bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500">
            <li>
              · <b className="font-medium text-gray-600">내용이 기입되지 않은 빈 양식(뼈대 서식)이 가장 정확합니다.</b>{' '}
              작성 완료된 보고서를 올리면 기입된 값이 양식 문구로 섞여 들어갈 수 있습니다.
            </li>
            <li>
              · 업로드할 때마다 <b className="font-medium text-gray-600">새 초안 템플릿이 생성</b>됩니다.
              이전 초안에서 한 데이터 연결·다듬기 작업은 새 초안에 승계되지 않습니다.
            </li>
            {/* ⚠️ 아래 세 용량(전역 20MB · 엑셀 5MB · 한글 10MB)은 **서버 상한의 복사본**이다 —
                여기서 숫자를 바꾸면 서버가 거부하는 파일을 「올릴 수 있다」고 안내하게 된다.
                정본: multer 전역 상한(`admin-report-composer.controller.ts`) ·
                `MAX_XLSX_BYTES`·`MAX_HWP_BYTES`(`report-composer` 서비스).
                이 축의 탐지기는 `formImportSupportedFormats.guard.test.ts`의
                「양식 가져오기 용량 문구」 describe다 — 그 가드가 **서버 소스에서 값을 읽어**
                이 문구와 대조하므로, 한쪽만 바꾸면 red가 된다(2026-10-06 리뷰: 리터럴이라
                지적됐으나 소스 계약은 이미 그 가드가 쥐고 있다. 매뉴얼 문구까지 함께 센다). */}
            <li>· JPEG · PNG · PDF · 엑셀(.xlsx) · 한글(.hwp)을 올릴 수 있습니다 (최대 20MB).</li>
            <li>· PDF는 10페이지까지 분석합니다.</li>
            <li>
              · 엑셀은 격자(표 구조·병합·문구)를 파일에서 그대로 읽어{' '}
              <b className="font-medium text-gray-600">이미지보다 정확</b>합니다. 시트가 여러 개면
              변환할 시트를 고를 수 있고, 여러 개를 고르면 차례로 변환해 시트마다 초안이 하나씩
              생깁니다(아무것도 고르지 않으면 내용이 있는 첫 시트 하나만). 엑셀은 5MB까지 올릴 수
              있습니다.
            </li>
            <li>
              · 한글(.hwp)도 표 구조·문구를 파일에서 그대로 읽어{' '}
              <b className="font-medium text-gray-600">이미지보다 정확</b>합니다. 표가 여러 개여도
              문서에 적힌 순서대로 하나의 초안이 됩니다(엑셀처럼 고를 시트는 없습니다). 한글은
              10MB까지 올릴 수 있습니다.
            </li>
            <li>
              · HWPX(.hwpx)·구형 한글(HWP 3.0)은 한글에서{' '}
              <b className="font-medium text-gray-600">다른 이름으로 저장 → 한글 문서(*.hwp)</b>로,
              구형 엑셀(.xls)은 xlsx로 저장 후 업로드해주세요.
            </li>
            <li>· 글자가 또렷할수록 인식이 정확합니다.</li>
          </ul>
        </div>
      ) : null}

      {importing ? (
        <div className="space-y-3 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 text-sm font-medium text-gray-700">
              {batch ? (
                <>
                  <span className="tabular-nums">
                    시트 {batch.current}/{batch.total}
                  </span>
                  {batch.currentSheetName ? (
                    <span className="ml-1.5 font-normal text-gray-500">
                      {batch.currentSheetName}
                    </span>
                  ) : null}
                  <span className="ml-1.5 font-normal text-gray-500">
                    · {STAGE_LABELS[stage]}
                  </span>
                </>
              ) : (
                STAGE_LABELS[stage]
              )}
            </span>
            <span className="shrink-0 text-sm tabular-nums text-gray-500">
              {formatElapsed(elapsedSeconds)} 경과
            </span>
          </div>
          {/* 배치는 analyzing 구간이어도 k/N이 정직하므로 확정 바를 쓴다 — 단건에서는 서버가
              그 구간의 진행을 몰라 30%에서 멈춘 것처럼 보이므로 불확정 바로 대체한다. */}
          {waitingOnAi && !batch ? (
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-label="AI 분석 진행 중"
            >
              <div className="h-full w-1/3 rounded-full bg-brand-600 motion-safe:animate-indeterminate-bar" />
            </div>
          ) : (
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-brand-600 transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
          {batch && (batch.completed > 0 || batch.failed > 0) ? (
            <p className="text-xs tabular-nums text-gray-500">
              지금까지 초안 {batch.completed}개 생성
              {batch.failed > 0 ? ` · ${batch.failed}건 실패` : ''} — 실패한 시트가 있어도 나머지는
              계속 진행합니다.
            </p>
          ) : null}
          {cancelling ? (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
              취소를 요청했습니다 — <b className="font-medium">처리 중인 시트가 끝나면 중단</b>
              됩니다(최대 1분). 그때까지 만들어진 초안은 지워지지 않습니다.
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-gray-500">
              {progress?.provider
                ? `${progress.provider}${progress.model ? `(${progress.model})` : ''}로 분석 중 — `
                : ''}
              {batch
                ? '시트당 약 1분이 걸립니다. '
                : '양식이 복잡하거나 상위 모델을 쓰면 최대 5분까지 걸릴 수 있습니다. '}
              창을 닫아도 분석은 계속되며, 진행 상황은 상단 표시에서 볼 수 있습니다. 완료되면
              알림이 뜨고, 상단 표시를 눌러 결과를 확인할 수 있습니다.
            </p>
          )}
        </div>
      ) : null}

      {phase === 'result' && result ? (
        <div className="space-y-3">
          {warning ? (
            <p className="text-xs font-medium leading-relaxed text-amber-600">{warning}</p>
          ) : null}
          {isBatchResult ? (
            // 여러 건이 만들어졌다는 사실 + 초안별 목록. 각 항목은 요약 한 줄(입력/표/제안 수)이
            // 기본이고 상세(제안 문구·확인 필요 항목)는 접어 둔다 — 47시트가 실측 사례라
            // 상세를 펼쳐 두면 목록이 화면을 몇 번씩 넘겨 어느 초안을 열지 고를 수 없게 된다.
            <>
              <p
                className={
                  progress?.status === 'cancelled'
                    ? 'rounded bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-800'
                    : 'rounded bg-green-50 px-3 py-2 text-sm leading-relaxed text-green-800'
                }
              >
                {progress?.status === 'cancelled' ? '변환을 취소했습니다 — ' : ''}
                <b>{describeFormImportOutcome(progress)}</b>. 초안은 모두 <b>비공개</b> 상태입니다.
                열고 싶은 초안을 아래에서 고르세요.
              </p>
              {outcomeList}
              <p className="text-xs leading-relaxed text-gray-500">
                <b className="font-medium">반복 제안 적용</b>은 초안을 열지 않고 그 자리에서 반복영역
                + 행 데이터셋 골격을 만들어 저장합니다(작성 페이지 위젯 배치는 에디터에서 마무리).
                초안을 하나 열면 이 결과 목록은 사라집니다 — 나머지 초안은 템플릿 목록에서 계속
                찾을 수 있습니다(모두 비공개 상태).
              </p>
            </>
          ) : (
            <p className="rounded bg-green-50 px-3 py-2 text-sm leading-relaxed text-green-800">
              템플릿 초안을 만들었습니다 — 입력 필드 <b>{result.summary.fieldCount}개</b>, 표{' '}
              <b>{result.summary.tableCount}개</b>. 초안은 <b>비공개</b> 상태이며 key는{' '}
              <code className="rounded bg-white px-1 py-0.5 text-xs">{result.templateKey}</code>{' '}
              입니다.
            </p>
          )}

          {!isBatchResult && result.summary.repeatHints.length > 0 ? (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-800">
                반복 영역 제안 <span className="font-normal text-gray-400">(미적용)</span>
              </h3>
              <ul className="space-y-1 rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
                {result.summary.repeatHints.map((hint, i) => (
                  <li key={`${hint.rowStart}-${hint.rowEnd}-${i}`}>
                    · {hint.rowStart}~{hint.rowEnd}행이 반복 영역으로 보입니다 ({hint.reason}) —
                    에디터에서 반복 설정을 확인하세요.
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* 배치는 "확인이 필요한 항목"이 초안마다 달라서 대표 1건만 보이면 오히려 오해를
              부른다 — 각 초안을 열었을 때 그 초안의 목록을 보게 한다. */}
          {!isBatchResult && result.summary.unsupported.length > 0 ? (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-800">확인이 필요한 항목</h3>
              <ul className="space-y-1 rounded bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
                {result.summary.unsupported.map((item, i) => (
                  <li key={`${i}-${item}`}>· {item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {phase === 'error' ? (
        <div className="space-y-3">
          <p className="rounded bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700">
            {errorHeadline}
          </p>
          {/* 시트별 사유 — 47시트가 전부 실패해도 어느 시트가 왜 실패했는지 보여 준다.
              (결과 화면과 같은 목록. 실패 항목엔 열기/적용 버튼이 그려지지 않는다.) */}
          {isBatchFailure ? outcomeList : null}
          {rawResponse ? (
            <details className="rounded border border-gray-200 px-3 py-2">
              <summary className="cursor-pointer text-xs text-gray-500">AI 응답 원문 보기</summary>
              <div className="mt-2 space-y-2">
                <button
                  type="button"
                  onClick={handleCopyRaw}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
                >
                  {rawCopied ? '복사됨' : '원문 복사'}
                </button>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all text-xs text-gray-600">
                  {rawResponse}
                </pre>
              </div>
            </details>
          ) : null}
          <p className="text-xs leading-relaxed text-gray-500">
            같은 양식의 엑셀(.xlsx)이나 한글(.hwp) 원본이 있으면 그것으로 다시 시도해보세요 —
            표 구조를 파일에서 그대로 읽어 이미지·PDF보다 정확합니다. 이미지·PDF뿐이라면 표가
            또렷한 파일로 다시 시도해보세요. 반복 실패 시 AI 프롬프트(FORM_IMPORT) 설정을
            확인해주세요.
          </p>
        </div>
      ) : null}
    </Modal>
  );
}
